// Pieces of the label drawing that both label makers share. Every function returns HTML text with inline styles (inline so the
// PNG/PDF export looks exactly like the preview). Ported unchanged from the original pages: the parity suite compares every
// exported label pixel for pixel with the old app's (test/parity/golden/labels.json), so change nothing here by accident.
import type { LabelData } from '../model/types';

const _LINK_RE = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(:\d+)?([/?#]\S*)?$/i;
export function cleanQrText(text: string): string {
  const t = text.trim();
  const q = t.indexOf('?');
  if(q < 0 || !_LINK_RE.test(t)) return t;
  const variant = new URLSearchParams(t.slice(q + 1).split('#')[0]).get('variant');
  return (t.slice(0, q) + (variant ? '?variant=' + encodeURIComponent(variant.trim()) : '')).trim();
}

export const _C39: Record<string, string> = {
  '0':'000110100','1':'100100001','2':'001100001','3':'101100000',
  '4':'000110001','5':'100110000','6':'001110000','7':'000100101',
  '8':'100100100','9':'001100100',
  'A':'100001001','B':'001001001','C':'101001000','D':'000011001',
  'E':'100011000','F':'001011000','G':'000001101','H':'100001100',
  'I':'001001100','J':'000011100','K':'100000011','L':'001000011',
  'M':'101000010','N':'000010011','O':'100010010','P':'001010010',
  'Q':'000000111','R':'100000110','S':'001000110','T':'000010110',
  'U':'110000001','V':'011000001','W':'111000000','X':'010010001',
  'Y':'110010000','Z':'011010000',
  '-':'010000101','.':'110000100',' ':'011000100',
  '$':'010101000','/':'010100010','+':'010001010','%':'000101010',
  '*':'010010100'
};

export function arrowSvg(direction: string, sizePx: number): string {
  if(!direction || direction === 'none') return '';
  const rot = {up:0, right:90, down:180, left:270}[direction] || 0;
  return `<svg width="${sizePx}" height="${sizePx}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" style="display:block;transform:rotate(${rot}deg);">
    <polygon points="50,18 86,78 14,78" fill="#000" stroke="#000" stroke-width="10" stroke-linejoin="round" stroke-linecap="round"/>
  </svg>`;
}

export function _hasSlot(qrSrc: string | null, direction: string): boolean {
  return !!qrSrc || Boolean(direction && direction !== 'none');
}

export function qrAbs(src: string | null, px: number, _FO: string, direction: string): string {
  const showArrow = direction && direction !== 'none';
  const showQR    = !!src;
  if(!showArrow && !showQR) return '';
  const boxW   = px + 8;
  const scanFs = Math.max(6, Math.round(px * 0.095));
  const arrowGap = showArrow && showQR ? 4 : 0;
  return `<div style="display:inline-flex;flex-direction:column;align-items:center;width:${boxW}px;overflow:hidden;">
    ${showArrow ? `<div style="width:${px}px;height:${px}px;margin-bottom:${arrowGap}px;display:flex;align-items:center;justify-content:center;">${arrowSvg(direction, px)}</div>` : ''}
    ${showQR ? `<div style="border:1px solid #ccc;padding:3px;background:#fff;width:${boxW}px;box-sizing:border-box;">
      <img src="${src}" width="${px}" height="${px}" style="display:block;">
    </div>
    <div style="font-family:open-sans,sans-serif;width:${boxW}px;text-align:center;font-size:${scanFs}px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:#333;margin-top:3px;overflow:hidden;white-space:nowrap;">SCAN FOR DETAILS</div>` : ''}
  </div>`;
}

export function fmt$(val: string): string {
  if(!val) return val;
  return /^[$€£¥]/.test(val.trim()) ? val : '$' + val;
}

export const TITLE_STEPS: [number, number][] = [[30, 1.15], [60, 0.9], [Infinity, 0.72]];

export function isOnSale(v: Pick<LabelData, 'price' | 'compare'>): boolean {
  const num = (t: string) => parseFloat(String(t || '').replace(/[^0-9.]/g, ''));
  const price = num(v.price), compare = num(v.compare);
  return isFinite(price) && isFinite(compare) && compare > price;
}

export function saleBadge(v: Pick<LabelData, 'price' | 'compare'>, fs: number, s: number, FC: string): string {
  if(!isOnSale(v)) return '';
  return `<span style="flex-basis:100%;display:block;margin-top:${Math.round(fs*0.45)}px;margin-bottom:${Math.round(6*s)}px;line-height:1;"><span style="display:inline-block;padding:${Math.round(fs*0.28)}px ${Math.round(fs*0.55)}px ${Math.round(fs*0.2)}px;background:#000;color:#fff;border-radius:${Math.round(fs*0.3)}px;font-family:${FC};font-size:${fs}px;font-weight:400;letter-spacing:${Math.max(1, Math.round(fs*0.12))}px;line-height:1;text-transform:uppercase;white-space:nowrap;">Sale</span></span>`;
}

export function makeGutterLines(cols: number, rows: number, labelW: number, labelH: number, pageW: number, pageH: number, MARGIN: number, GAP: number): string {
  if(cols === 1 && rows === 1) return '';
  let svg = `<svg style="position:absolute;top:0;left:0;pointer-events:none;" width="${pageW}" height="${pageH}" xmlns="http://www.w3.org/2000/svg">`;

  const dashStyle = 'stroke:#bbb;stroke-width:0.75;stroke-dasharray:4,4;';

  // Vertical gutters
  for(let c = 1; c < cols; c++){
    const x = MARGIN + c*(labelW) + (c-1)*GAP + GAP/2;
    svg += `<line x1="${x}" y1="${MARGIN}" x2="${x}" y2="${pageH-MARGIN}" style="${dashStyle}"/>`;
  }

  // Horizontal gutters
  for(let r = 1; r < rows; r++){
    const y = MARGIN + r*(labelH) + (r-1)*GAP + GAP/2;
    svg += `<line x1="${MARGIN}" y1="${y}" x2="${pageW-MARGIN}" y2="${y}" style="${dashStyle}"/>`;
  }

  svg += '</svg>';
  return svg;
}
